'use client';

// Wrapper CLIENT pour le rendu d'une page CMS (/[slug]).
//
// Reçoit `initialPage` + `initialSettings` rendus au build (SSG) — le HTML
// statique contient déjà l'intégralité du contenu pour le SEO Google.
// Au runtime côté navigateur (après hydratation), on REFETCHE la page depuis
// l'API pour récupérer les éventuelles modifications faites dans le
// super-admin SANS attendre un redéploiement.
//
// Conséquence pratique : il suffit de cliquer "Publier sur le site" UNE
// SEULE fois (à la création de la page) — toutes les éditions ultérieures
// apparaîtront immédiatement après "Enregistrer", au prochain refresh
// navigateur. Comportement identique à celui d'avant la conversion en
// Server Component.

// V1.25.938 — Suppression TOTALE du refetch client (cause racine du bug
// "1 clic sur 2"). Le rendu est 100% base sur les props server-rendered.
// L'auto-rebuild watcher regenere le SSG quand admin edite (~90s) → les
// modifs apparaissent au prochain refresh.
import ThemeInjector from '@/components/ThemeInjector';
import FaviconInjector from '@/components/FaviconInjector';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { BlocksList } from '@/components/blocks/BlockRenderer';

export default function CmsPageContent({
  slug,
  initialPage,
  initialSettings,
  initialNavPages = [],
  initialFooterPages = [],
  legalLinks = [],
}) {
  // V1.25.938 — Pas de useState/useEffect. Tout vient des props SSR.
  void slug;
  const page = initialPage;
  const settings = initialSettings;

  if (!page) return null; // Le wrapper serveur a déjà rendu le 404 statique.

  const blocks = Array.isArray(page.blocks) ? page.blocks : [];
  // 2026-07 — L'en-tete auto (H1 titre + "Mis a jour le ...") est SUPPRIME
  // pour les pages standard (page_type="page") : elles sont construites avec
  // le page builder et portent deja leur propre titre (hero, heading...).
  // On le garde uniquement pour les pages legales/module/blog qui n'ont pas
  // de hero, sans la ligne de date.
  const hasHero = blocks.some(b => b && b.type === 'hero');
  const showAutoHeader = !hasHero && page.page_type && page.page_type !== 'page';

  // 2026-07 — Landing page (page_type="landing") : AUCUN Navbar/Footer
  // standard, pas de header auto avec titre/date. 100% de l'attention sur
  // le contenu et le CTA (campagnes Google Ads). Seul un micro-footer avec
  // les liens légaux (CGV/confidentialité) est affiché : exigence Google Ads
  // + obligation légale, sans offrir de porte de sortie vers le site.
  if (page.page_type === 'landing') {
    return (
      <>
        <ThemeInjector settings={settings} />
        <FaviconInjector faviconUrl={settings.favicon_url} />
        <main>
          <BlocksList blocks={blocks} settings={settings} />
        </main>
        <footer style={{
          padding: '24px 16px',
          textAlign: 'center',
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          borderTop: '1px solid var(--color-border)',
        }}>
          <span>© {new Date().getFullYear()} Ligobox</span>
          {legalLinks.map(l => (
            <a
              key={l.slug}
              href={`/${l.slug}`}
              style={{ marginLeft: '16px', color: 'var(--color-text-muted)', textDecoration: 'underline' }}
            >
              {l.title}
            </a>
          ))}
        </footer>
      </>
    );
  }

  return (
    <>
      <ThemeInjector settings={settings} />
      <FaviconInjector faviconUrl={settings.favicon_url} />
      <Navbar settings={settings} initialCmsPages={initialNavPages} isHomePage={false} />

      <main>
        {showAutoHeader && (
          <header style={{
            background: 'var(--color-bg-alt)',
            padding: '60px 0 40px',
            borderBottom: '1px solid var(--color-border)',
          }}>
            <div className="container-page" style={{ maxWidth: '900px' }}>
              {page.page_type && page.page_type !== 'page' && (
                <span style={{
                  display: 'inline-block', padding: '4px 12px', borderRadius: '999px',
                  background: 'rgba(226, 114, 40, 0.1)', color: 'var(--color-primary)',
                  fontSize: '12px', fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  marginBottom: '12px',
                }}>{labelForType(page.page_type)}</span>
              )}
              <h1 style={{
                fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800,
                lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 12px', color: 'var(--color-text)',
              }}>{page.title}</h1>
            </div>
          </header>
        )}

        <BlocksList blocks={blocks} settings={settings} />
      </main>

      <Footer settings={settings} initialCmsPages={initialFooterPages} />
    </>
  );
}

function labelForType(type) {
  const map = {
    legal: 'Mentions légales',
    module: 'Module',
    blog: 'Article',
    page: '',
  };
  return map[type] || '';
}
